import { useOrganization, useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { useApi } from "@/api/useApi";

// Maps the active Clerk organization role to app-level permissions.
// Roles: org:admin (full control) | org:editor (create/QA, approve/decline) | org:member (Reviewer — feedback only)
// Clerk's current plan has no real 3rd org role, so a 'member' can be promoted to Editor via an
// app-level override (Team page) — GET /api/me resolves that override server-side.

// Many components call useRole() simultaneously on first load — dedupe/cache the /api/me lookup
// per org+user for a few seconds instead of firing one request per mounted component.
const meCache = new Map(); // key -> { promise, ts }
const ME_TTL_MS = 5000;
function fetchMe(api, key) {
  const cached = meCache.get(key);
  if (cached && Date.now() - cached.ts < ME_TTL_MS) return cached.promise;
  const promise = api.get("/me").then((res) => res.data?.role);
  meCache.set(key, { promise, ts: Date.now() });
  return promise;
}

export function useRole() {
  const { membership, isLoaded: orgLoaded } = useOrganization();
  const { user } = useUser();
  const api = useApi();
  const clerkRole = membership?.role || null;
  const [role, setRole] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);

  useEffect(() => {
    if (!orgLoaded) return;
    if (clerkRole !== "org:member") {
      setRole(clerkRole);
      setRoleLoaded(true);
      return;
    }
    let active = true;
    setRoleLoaded(false);
    fetchMe(api, `${membership?.organization?.id || ""}:${user?.id || ""}`).then((resolved) => {
      if (active) { setRole(resolved || clerkRole); setRoleLoaded(true); }
    }).catch(() => { if (active) { setRole(clerkRole); setRoleLoaded(true); } });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkRole, orgLoaded, user?.id]);

  const isLoaded = orgLoaded && roleLoaded;
  return {
    isLoaded,
    role,
    isAdmin: role === "org:admin",
    isEditor: role === "org:editor",
    isMember: role === "org:member",
    canManageContent: role === "org:admin" || role === "org:editor",
    canApprove: role === "org:admin" || role === "org:editor",
    canRequestRevision: role === "org:admin" || role === "org:editor",
    canDelete: role === "org:admin",
    canManageMedia: role === "org:admin" || role === "org:editor",
    canDeleteMedia: role === "org:admin" || role === "org:editor",
    canSeeAnalytics: role === "org:admin",
    canManageTeam: role === "org:admin",
    canManageSettings: role === "org:admin",
  };
}

export const ROLE_LABELS = {
  "org:admin": "Admin",
  "org:editor": "Editor / QA",
  "org:member": "Reviewer",
};
